import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Lock } from '@/components/Icons.tsx';
import { TypoBody, TypoCallout, TypoCaption, TypoFootnote, TypoHeading2 } from '@/components/Typography.tsx';
import LADDER_MY_MATCHES from '@/graphql/queries/ladder-my-matches.graphql';
import LADDER_SOLO_SETS from '@/graphql/queries/ladder-solo-sets.graphql';
import LADDER_QUESTION_VAULT from '@/graphql/queries/ladder-question-vault.graphql';
import LADDER_LEADERBOARD from '@/graphql/queries/ladder-leaderboard.graphql';

import {
  LadderMatch, LadderStats, LadderVaultItem, PublicPlayer, SoloSet, SoloSets, VaultGroup, MATCH_MODE_LABELS,
  POOL_LABELS, TONE_TEXT, formatDate, formatPercent, formatRelative, formatStreak, getIdleTone, getMatchSides,
  getReviewPath, getSoloResult, useLadderProfile, useMyClientId,
} from './common.ts';
import { AppNote, Badge, InlineLoader, Panel, PanelLink, PlayerAvatar, ReviewLink, RoundBars } from './components.tsx';

const LadderOverview = () => {
  const { stats, loading } = useLadderProfile();

  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-6 cq-xl:grid-cols-3">
        <NextMatchCard stats={stats} className="cq-xl:col-span-2" />
        <RankCard stats={stats} loading={loading} />
      </div>
      <SoloRunway />
      <div className="grid gap-6 cq-xl:grid-cols-3">
        <VaultSpotlight className="cq-xl:col-span-2" />
        <StandingsPreview myRank={stats?.rank ?? null} />
      </div>
    </div>
  );
};

/** The one thing to do next: an H2H turn, a pending challenge, or the next solo match. */
const NextMatchCard = ({ stats, className = '' }: { stats?: LadderStats; className?: string }) => {
  const myId = useMyClientId();
  const { data: matchData, loading } = useQuery(LADDER_MY_MATCHES, {
    variables: { status: 'ACTIVE', limit: 10 },
  });
  const { data: soloData } = useQuery(LADDER_SOLO_SETS);

  const matches: LadderMatch[] = matchData?.ladderMyMatches ?? [];
  const match = matches.find(m => m.currentTurnClientId === myId) ?? matches[0];
  const nextSolo = (soloData?.ladderSoloSets?.sets as SoloSet[] | undefined)?.find(set => set.unlocked && !set.played);

  return (
    <Panel className={`flex flex-col justify-between gap-6 ${className}`}>
      {loading ? <InlineLoader /> : match ? (
        <ActiveMatch match={match} myId={myId} />
      ) : (
        <div className="flex flex-col gap-3">
          <Badge tone="info" className="self-start">
            {stats?.pendingChallenges ? 'Pending challenges' : 'No active match'}
          </Badge>
          <TypoHeading2 className="text-neutral2-text-strong font-medium m:text-heading2-m">
            {nextSolo ? `Solo Match #${nextSolo.position} is up next` : 'Ready for your next battle?'}
          </TypoHeading2>
          <TypoBody className="text-neutral2-text-soft">
            {stats?.pendingChallenges
              ? `You have ${stats.pendingChallenges} pending challenge${stats.pendingChallenges > 1 ? 's' : ''}. `
                + 'Open the app to answer them before they expire.'
              : 'Challenge someone above you on the ladder or keep your solo streak going.'}
          </TypoBody>
          {nextSolo && nextSolo.answers.length > 0 && (
            <div className="flex items-center gap-3">
              <RoundBars results={nextSolo.roundsWon} total={5} />
              <TypoCaption className="text-neutral2-text-softer">
                Round {nextSolo.answers.length + 1} of 5
              </TypoCaption>
            </div>
          )}
        </div>
      )}
      <div className="pt-4 border-t border-shade-10">
        <AppNote />
      </div>
    </Panel>
  );
};

const ActiveMatch = ({ match, myId }: { match: LadderMatch; myId: number }) => {
  const { them, myScore, theirScore, isMyTurn, rounds } = getMatchSides(match, myId);
  const deadline = formatRelative(match.turnDeadlineAt);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone="info">Active {MATCH_MODE_LABELS[match.matchMode]} match</Badge>
        {match.isRankWar && <Badge tone="warning">Rank war</Badge>}
        {match.climbIfWin ? <Badge tone="success">Win to climb {match.climbIfWin}</Badge> : null}
      </div>
      <TypoHeading2 className="text-neutral2-text-strong font-medium m:text-heading2-m">
        {isMyTurn ? 'It’s your turn!' : `Waiting on ${them?.ladderName ?? 'your opponent'}`}
      </TypoHeading2>
      <div className="flex items-center gap-4">
        <PlayerAvatar player={them} />
        <div className="flex-1 min-w-0">
          <TypoCallout className="block truncate text-neutral2-text-strong font-semibold">
            vs {them?.ladderName ?? 'Unknown player'}
          </TypoCallout>
          <TypoCaption className="text-neutral2-text-softer">
            Round {match.currentRound}{deadline ? ` · turn ends ${deadline}` : ''}
          </TypoCaption>
        </div>
        <TypoHeading2 className="text-neutral2-text-strong font-bold whitespace-nowrap">
          {myScore} – {theirScore}
        </TypoHeading2>
      </div>
      <RoundBars results={rounds.filter(r => r.resolved).map(r => (r.won ? true : r.lost ? false : null))} total={3} />
    </div>
  );
};

const RankCard = ({ stats, loading }: { stats?: LadderStats; loading: boolean }) => (
  <Panel className="flex flex-col gap-4">
    {loading || !stats ? <InlineLoader /> : (
      <>
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 shrink-0 rounded-lg bg-neutral3 flex flex-col items-center justify-center">
            <strong className="text-heading2 leading-none text-neutral3-text-strong">{stats.rank ?? '–'}</strong>
            <TypoCaption className="uppercase font-semibold text-neutral3-text-soft">Rank</TypoCaption>
          </div>
          <div>
            <TypoCallout className="block text-neutral2-text-strong font-semibold">
              {stats.rank ? `Rank ${stats.rank} of ${stats.rankedPlayers}` : 'Unranked'}
            </TypoCallout>
            <TypoCaption className="text-neutral2-text-softer">
              {stats.rank
                ? 'Challenge someone above you to climb.'
                : 'Win a ranked match to join the bottom rank.'}
            </TypoCaption>
          </div>
        </div>
        <dl className="grid grid-cols-2 gap-3">
          <Stat label="Wins" value={stats.wins} tone="success" />
          <Stat label="Losses" value={stats.losses} tone="danger" />
          <Stat label="Streak" value={formatStreak(stats.streak)} />
          <Stat
            label="Days idle"
            value={stats.daysIdle ?? '—'}
            tone={getIdleTone(stats.daysIdle)}
          />
        </dl>
        {(stats.daysIdle ?? 0) >= 4 && (
          <TypoCaption className={TONE_TEXT[getIdleTone(stats.daysIdle)]}>
            Ranks drop after 7 idle days. Play a match to stay on your rank.
          </TypoCaption>
        )}
      </>
    )}
  </Panel>
);

const Stat = ({ label, value, tone }: { label: string; value: string | number; tone?: keyof typeof TONE_TEXT }) => (
  <div className="rounded-sm bg-shade-5 px-3 py-2">
    <dt><TypoCaption className="text-neutral2-text-softer">{label}</TypoCaption></dt>
    <dd className={`text-body font-semibold ${tone ? TONE_TEXT[tone] : 'text-neutral2-text-strong'}`}>{value}</dd>
  </div>
);

const RUNWAY_SIZE = 6;

const SoloRunway = () => {
  const { data, loading } = useQuery(LADDER_SOLO_SETS);
  const solo: SoloSets | undefined = data?.ladderSoloSets;
  const sets = [...(solo?.sets ?? [])].sort((a, b) => a.position - b.position);
  const nextIdx = sets.findIndex(set => set.unlocked && !set.played);
  const anchor = nextIdx === -1 ? sets.length : nextIdx;
  const start = Math.max(0, Math.min(anchor - Math.floor(RUNWAY_SIZE / 2), sets.length - RUNWAY_SIZE));
  const runway = sets.slice(start, start + RUNWAY_SIZE);

  return (
    <Panel title="SOLO MODE MATCHES" action={<PanelLink to="/ladder/solo">All solo matches</PanelLink>}>
      {loading ? <InlineLoader /> : (
        <>
          {solo && (
            <div className="flex flex-wrap gap-x-8 gap-y-2 mb-5">
              <InlineStat label="Played" value={solo.played} />
              <InlineStat label="Won" value={solo.won} className={TONE_TEXT.success} />
              <InlineStat label="Lost" value={solo.lost} className={TONE_TEXT.danger} />
              <InlineStat label="Correct answers" value={solo.rightAnswers} className={TONE_TEXT.success} />
              <InlineStat label="Incorrect answers" value={solo.wrongAnswers} className={TONE_TEXT.danger} />
            </div>
          )}
          <div className="grid grid-cols-3 cq-lg:grid-cols-6 gap-3">
            {runway.map(set => (
              <RunwayTile key={set.position} set={set} isNext={set.position === sets[nextIdx]?.position} />
            ))}
          </div>
        </>
      )}
    </Panel>
  );
};

const InlineStat = ({ label, value, className = 'text-neutral2-text-strong' }: {
  label: string;
  value: number;
  className?: string;
}) => (
  <div>
    <TypoCaption className="block text-neutral2-text-softer">{label}</TypoCaption>
    <strong className={`text-heading ${className}`}>{value}</strong>
  </div>
);

const RunwayTile = ({ set, isNext }: { set: SoloSet; isNext: boolean }) => {
  const result = getSoloResult(set);
  const locked = !set.unlocked && !set.played;

  return (
    <Link
      to="/ladder/solo"
      className={`flex flex-col items-center justify-between gap-1 h-28 p-3 rounded-lg text-center
        ${isNext ? 'bg-neutral3 ring-2 ring-primary' : 'bg-neutral hover:bg-neutral-hover'}
        ${locked ? 'opacity-40' : ''}`}
    >
      <TypoCaption className={`font-semibold uppercase ${isNext ? 'text-primary' : TONE_TEXT[result.tone]}`}>
        {isNext && (set.answers.length ? 'In progress' : 'Up next')}
        {!isNext && (locked ? <Lock width={14} height={14} /> : result.label)}
      </TypoCaption>
      <strong className="text-heading text-neutral-text-strong">{set.position}</strong>
      {set.played || set.answers.length
        ? <RoundBars results={set.roundsWon} total={5} />
        : <TypoCaption className="text-neutral-text-weak">{locked ? 'Locked' : 'Ready'}</TypoCaption>}
    </Link>
  );
};

const VaultSpotlight = ({ className = '' }: { className?: string }) => {
  const { data, loading } = useQuery(LADDER_QUESTION_VAULT);
  const vault = data?.ladderQuestionVault;
  const groups: VaultGroup[] = (vault?.groups ?? []).filter((group: VaultGroup) => group.studycenterGroupId !== null);
  const needsReview: LadderVaultItem[] = (vault?.needsReview ?? []).slice(0, 3);

  return (
    <Panel
      title="Question Review Vault"
      action={<PanelLink to="/ladder/vault">Open the vault</PanelLink>}
      className={className}
    >
      {loading ? <InlineLoader /> : (
        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-1 cq-md:grid-cols-3 gap-3">
            {groups.map(group => (
              <Link
                key={group.name}
                to={`/ladder/vault?group=${encodeURIComponent(group.name)}`}
                className="flex flex-col gap-1 p-3 rounded-lg bg-neutral hover:bg-neutral-hover"
              >
                <div className="flex items-center justify-between gap-2">
                  <TypoCallout className="font-semibold text-neutral-text-strong">{group.name}</TypoCallout>
                  <Badge>{group.categories.length} types</Badge>
                </div>
                <strong className="text-heading text-neutral-text-strong">
                  {formatPercent(group.correct, group.answered)}
                  <TypoCaption className="ml-1 font-normal text-neutral-text-softer">accuracy</TypoCaption>
                </strong>
                <TypoCaption className="text-neutral-text-softer">{group.answered} answered</TypoCaption>
              </Link>
            ))}
          </div>
          <div className="flex flex-col gap-2">
            <TypoFootnote className="font-semibold uppercase text-neutral2-text-strong">Needs review</TypoFootnote>
            {needsReview.length ? needsReview.map(item => (
              <div
                key={item.clientExamQuestionId}
                className="flex items-center justify-between gap-3 p-3 rounded-lg bg-neutral"
              >
                <div className="min-w-0">
                  <TypoCallout className="block truncate font-semibold text-neutral-text-strong">
                    {item.label}
                  </TypoCallout>
                  <TypoCaption className="text-neutral-text-softer">
                    {POOL_LABELS[item.pool]} · {formatDate(item.playedAt)}
                  </TypoCaption>
                </div>
                <ReviewLink to={getReviewPath(item)} />
              </div>
            )) : (
              <TypoCaption className="text-neutral2-text-softer">
                Nothing to review. Every latest answer was right!
              </TypoCaption>
            )}
          </div>
        </div>
      )}
    </Panel>
  );
};

/** The top of the ladder plus the viewer's own neighbourhood. */
const StandingsPreview = ({ myRank }: { myRank: number | null }) => {
  const { data: topData, loading } = useQuery(LADDER_LEADERBOARD, { variables: { limit: 3, offset: 0 } });
  const { data: aroundData } = useQuery(LADDER_LEADERBOARD, {
    variables: { limit: 3, offset: Math.max(0, (myRank ?? 0) - 2) },
    skip: !myRank || myRank <= 3,
  });

  const rows: { isMe: boolean; player: PublicPlayer }[] = [];
  [...(topData?.ladderLeaderboard ?? []), ...(aroundData?.ladderLeaderboard ?? [])].forEach(row => {
    if (!rows.some(existing => existing.player.clientId === row.player.clientId)) rows.push(row);
  });

  return (
    <Panel title="Ladder Standings" action={<PanelLink to="/ladder/rankings">Full rankings</PanelLink>}>
      {loading ? <InlineLoader /> : (
        <ol className="flex flex-col gap-2">
          {rows.map(({ isMe, player }, idx) => (
            <li key={player.clientId}>
              {idx > 0 && rows[idx - 1].player.rank !== null && player.rank !== null
                && player.rank - (rows[idx - 1].player.rank ?? 0) > 1 && (
                <div className="text-center text-neutral2-text-weak leading-none pb-2">⋯</div>
              )}
              <div
                className={`flex items-center gap-3 px-3 py-2 rounded-sm
                  ${isMe ? 'bg-neutral3 ring-1 ring-primary' : 'bg-shade-5'}`}
              >
                <strong
                  className={`w-6 text-callout ${player.rank === 1 ? 'text-warning' : 'text-neutral2-text-soft'}`}
                >
                  {player.rank}
                </strong>
                <PlayerAvatar player={player} size="sm" />
                <TypoCallout className="flex-1 min-w-0 truncate text-neutral2-text-strong font-medium">
                  {player.ladderName}{isMe && ' (You)'}
                </TypoCallout>
                <TypoCaption className="text-neutral2-text-softer whitespace-nowrap">
                  {player.wins}W – {player.losses}L
                </TypoCaption>
              </div>
            </li>
          ))}
          {!rows.length && <TypoCaption className="text-neutral2-text-softer">No ranked players yet.</TypoCaption>}
        </ol>
      )}
    </Panel>
  );
};

export default LadderOverview;
